export function waitImagesLoaded(root: HTMLElement): Promise<void[]> {
  return Promise.all(
    Array.from(root.querySelectorAll("img")).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((res) =>
            img.addEventListener("load", () => res(), { once: true }),
          ),
    ),
  );
}

/**
 * Remove temporariamente overflow de todos os descendentes para que o
 * html2canvas capture o conteúdo completo (sem clipping de tabelas responsivas).
 * Retorna uma função de restauração.
 */
function removeOverflowForCapture(root: HTMLElement): () => void {
  const snapshots: Array<{ el: HTMLElement; prev: string }> = [];
  root.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const cs = window.getComputedStyle(el);
    if (cs.overflow !== "visible" || cs.overflowX !== "visible") {
      snapshots.push({ el, prev: el.style.cssText });
      el.style.setProperty("overflow", "visible", "important");
      el.style.setProperty("overflow-x", "visible", "important");
    }
  });
  return () => snapshots.forEach(({ el, prev }) => (el.style.cssText = prev));
}

/**
 * Gera PDF de página única a partir do elemento visível.
 * Usa html2canvas para capturar o DOM completo (sem overflow clipping)
 * e jsPDF para montar o PDF com largura A4 e altura proporcional ao conteúdo.
 */
export async function gerarPdfExtratoBlob(
  element: HTMLElement,
  filename = "extrato.pdf",
): Promise<Blob> {
  await waitImagesLoaded(element);

  const restore = removeOverflowForCapture(element);

  try {
    const html2canvasLib = require("html2canvas");
    const html2canvas: (el: HTMLElement, opts: object) => Promise<HTMLCanvasElement> =
      html2canvasLib?.default ?? html2canvasLib;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
      scrollX: 0,
      windowWidth: element.scrollWidth,
      logging: false,
    });

    restore();

    // Dimensões em CSS px (canvas foi capturado a scale:2)
    const cssW = canvas.width / 2;
    const cssH = canvas.height / 2;

    // Página adapta-se ao conteúdo: sem escalar, texto em tamanho natural
    const MARGIN_MM = 6;
    const PX_TO_MM = 0.264583;
    const contentW_MM = cssW * PX_TO_MM;
    const contentH_MM = cssH * PX_TO_MM;
    const PAGE_W_MM = contentW_MM + MARGIN_MM * 2;
    const pageH_MM = contentH_MM + MARGIN_MM * 2;

    const jsPDFLib = require("jspdf");
    const JsPDF: new (opts: object) => {
      addImage: (data: string, fmt: string, x: number, y: number, w: number, h: number) => void;
      output: (type: string) => Blob;
      setProperties: (props: object) => void;
    } = jsPDFLib?.jsPDF ?? jsPDFLib?.default?.jsPDF ?? jsPDFLib;

    const doc = new JsPDF({
      unit: "mm",
      format: [PAGE_W_MM, pageH_MM],
      orientation: "portrait",
    });

    doc.setProperties({ title: filename });
    doc.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", MARGIN_MM, MARGIN_MM, contentW_MM, contentH_MM);

    return doc.output("blob");
  } catch (err) {
    restore();
    throw err;
  }
}

/** PdfResumo do extrato V2: mesma tela, página única. */
export const gerarPdfResumoExtratoBlob = (
  element: HTMLElement,
  solicitacaoId?: number | string | null,
) =>
  gerarPdfExtratoBlob(
    element,
    solicitacaoId
      ? `PdfResumo_extrato_${solicitacaoId}.pdf`
      : "PdfResumo.pdf",
  );
